import axios from "axios";

import env from "#/env";

const instance = axios.create({
  baseURL: env.API_URL,
  connectTimeout: 180000,
  timeout: 360000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  responseType: "json",
});

/**
 * Legacy API returns 200 status code even for errors, with error details in the response body.
 *
 *
 * @deprecated This function is no longer needed since the API now returns proper HTTP status codes for errors. It is retained for backward compatibility with older API versions that may still return 200 for errors, but can be removed once all API endpoints have been updated.
 * @param {object} response
 * @returns object
 */
function legacy200ErrorCheck(response) {
  // Skip error checking for blob responses (file downloads)
  if (response.config.responseType === "blob") {
    return response;
  }

  // Check if response contains error payload
  if (
    response.data &&
    response.data.result &&
    Array.isArray(response.data.result)
  ) {
    // NOTE: Warning-only responses pass through (only ERROR level triggers errors)
    const errorResults = response.data.result.filter(
      (item) => item.level === "ERROR"
    );
    if (errorResults.length > 0) {
      // Create custom error with API error payload
      const customError = new Error(errorResults[0].text || "API Error");
      customError.response = {
        status: 200, // API returns 200 for errors (but with error payload)
        data: {
          code: errorResults[0].code || "API_ERROR",
          message: errorResults[0].text || "API Error",
          errors: errorResults,
        },
      };
      throw customError;
    }
  }
}

/**
 * Placeholder for response interceptor
 *
 * Since the API now returns proper HTTP status codes for errors,
 * we can rely on axios's built-in error handling for non-2xx responses.
 *
 * Before that, this interceptor routed responses via legacy200ErrorCheck
 * to detect errors in 200 responses.
 *
 * Now, we just pass it through without modification,
 * but we keep the interceptor in place for potential future use.
 *
 * @param {object} response
 * @returns {object} The response object
 */
export function responseInterceptor(response) {
  // API now behaves properly and errors are returned with appropriate HTTP status codes,
  // so we can rely on axios error handling for non-2xx responses
  // and skip custom error checking in the response interceptor.
  return response;
}

/**
 * Placeholder for error interceptor
 *
 * We'll not be doing any extra logging here, browser's dev console will show
 * the error details as usual, and axios will handle the error propagation.
 *
 * @param {*} error
 * @returns {Promise<never>}
 */
export function errorInterceptor(error) {
  // Let axios handle other errors (network, 4xx, 5xx)

  return Promise.reject(error);
}

instance.interceptors.response.use(responseInterceptor, errorInterceptor);

/**
 * API client for making HTTP requests to the backend API.
 *
 * Provides methods to set authentication tokens and make requests.
 * It also includes a response interceptor to handle legacy API error formats,
 * but since the API now returns proper HTTP status codes, it relies on axios's
 * built-in error handling for non-2xx responses.
 *
 */
export default {
  setAuthToken(token) {
    instance.defaults.headers.common.Authorization = "Bearer " + token;
  },

  /**
   * Set the current authorization ID header for requests.
   *
   * @param {string} authorizationId
   */
  setAuthorizationId(authorizationId) {
    instance.defaults.headers.common["X-Zastopnistvo-Id"] = authorizationId;
  },
  request(options) {
    return instance.request(options);
  },
  get(url, params) {
    return instance.get(url, { params });
  },
  requestWithCredentials(options) {
    return instance.request({
      ...options,
      withCredentials: true,
    });
  },
};
